import {
  FocusEventHandler,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useWindowDimensions } from "../../customeHooks/useWindowDimensions";
import { Icon } from "../icon/icon";
import { ErrorText } from "../typography/errorText";

export const TextInput = ({
  type = "text",
  name = "",
  value = "",
  placeholder = "Placeholder",
  onChange,
  onBlur,
  onKeyDown = () => {},
  hasIcon = false,
  iconSvg = "",
  errors = "",
  rows = 3,
  inputClass = "",
  className = "",
  containerClassName = "",
  customIcon = undefined,
  customIconPosition = "left",
  hasTitle = false,
  title = "",
  autoComplete = "off",
  disabled = false,
  infoElement,
  readOnly = false,
  autoFocus = false,
  autoFocusOnMobile = true,
  pattern,
  ...inputProps
}: {
  type: string;
  name: string;
  value: string;
  placeholder: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  hasIcon?: boolean;
  iconSvg?: string;
  errors?: string;
  rows?: number;
  inputClass?: string;
  className?: string;
  containerClassName?: string;
  customIcon?: ReactNode;
  customIconPosition?: "left" | "right";
  hasTitle?: boolean;
  title?: string;
  autoComplete?: string;
  disabled?: boolean;
  infoElement?: ReactNode;
  readOnly?: boolean;
  autoFocus?: boolean;
  autoFocusOnMobile?: boolean;
  pattern?: string;
}) => {
  const [viewPassword, setViewPassword] = useState(false);
  const [focused, setFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const { width } = useWindowDimensions();

  const inputClassName = `
  bg-transparent w-full
  outline-none focus:outline-none
  font-bold
  placeholder:text-grey-700 placeholder:font-normal
  autofill:focus:bg-transparent autofill:bg-transparent
  ${inputClass}
  text-sm
  transition-all duration-300
  `;

  useEffect(() => {
    if (inputRef.current && autoFocus) {
      if (!autoFocusOnMobile && width < 768) {
        return;
      } else {
        inputRef.current.focus();
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const textarea = textAreaRef.current;
    if (textarea) {
      // Reset the height to auto to shrink if content is deleted
      textarea.style.height = "auto";
      // Set the height to match scrollHeight
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value]);

  return (
    <div className={containerClassName}>
      <div
        className={`group w-full items-center rounded-lg border border-grey-100 bg-grey-50 px-[12px] ${value && title ? "!py-2" : "py-[14px]"} text-sm font-normal focus-within:border-[2px] focus-within:border-grey-950 focus-within:py-[8px] md:py-[16px] ${errors && "border-[2px] focus-within:!border-danger"} transition-all ${className}`}
      >
        {hasTitle && (
          <div
            className={`hidden text-xs leading-4 text-grey-700 transition-all duration-300 group-focus-within:flex ${value && "!flex"}`}
          >
            {title}
          </div>
        )}
        <div className="flex flex-row gap-3">
          {hasIcon && !customIcon && (
            <Icon svg={iconSvg} width={20} height={20} className="w-[24px]" />
          )}
          {customIcon && customIconPosition === "left" ? customIcon : undefined}
          {type !== "textarea" ? (
            <input
              type={viewPassword ? "text" : type}
              name={name}
              value={value}
              placeholder={(focused && title) || !title ? placeholder : title}
              onChange={onChange}
              onBlur={(e) => {
                setFocused(false);
                onBlur(e);
              }}
              onFocus={() => {
                setFocused(true);
              }}
              onKeyDown={onKeyDown}
              className={inputClassName}
              autoComplete={autoComplete}
              disabled={disabled}
              readOnly={readOnly}
              ref={inputRef}
              pattern={pattern}
              {...inputProps}
            />
          ) : (
            <textarea
              name={name}
              value={value}
              placeholder={placeholder}
              onChange={onChange}
              onBlur={onBlur}
              rows={rows}
              disabled={disabled}
              className={`${inputClassName} h-auto resize-none`}
              readOnly={readOnly}
              onKeyDown={onKeyDown}
              ref={textAreaRef}
            />
          )}
          {type === "password" && (
            <button
              type="button"
              onClick={() => {
                setViewPassword(!viewPassword);
              }}
            >
              <Icon
                svg={viewPassword ? "hide-eye" : "show-eye"}
                width={24}
                height={24}
                className={`text-neut-700 w-[24px] cursor-pointer transition-all duration-500 ${viewPassword && "rotate-[180deg]"} `}
              />
            </button>
          )}
          {customIcon && customIconPosition === "right"
            ? customIcon
            : undefined}
        </div>
      </div>

      {errors && <ErrorText text={errors} />}

      {infoElement && (
        <div className="mt-2 text-xs !leading-5 text-primary">
          {infoElement}
        </div>
      )}
    </div>
  );
};
